import alert from '@jetbrains/ring-ui/components/alert-service/alert-service'
import {useCallback, useMemo} from 'react'
import {createSelector} from 'reselect'

import {useSetUserProperty} from '../../../../../hooks/useSetUserProperty'
import {useUserProperty} from '../../../../../hooks/useUserProperty'
import {internalProps} from '../../../../../types/BS_types'
import {UserProperties} from '../../../../../utils/userProperties'

const agetPoolFavoriteListSelector = createSelector([(property: string) => property], property =>
  property
    .split(',')
    .filter(id => id !== '')
    .map(Number),
)
export const useAgentPoolFavoriteList = () => {
  const value = useUserProperty(UserProperties.AGENT_POOL_FAVORITE_LIST) ?? ''
  return agetPoolFavoriteListSelector(value)
}

const defaultLimit = 30
export const useIsAgentPoolFavoriteLimitReached = () => {
  const limit = internalProps['teamcity.ui.agentPoolsFavoriteLimit'] ?? defaultLimit
  const agentPoolFavoriteList = useAgentPoolFavoriteList()

  return agentPoolFavoriteList.length >= limit
}

export const useIsAgentPoolFavorite = (agentPoolId: number) => {
  const agentPoolFavoriteList = useAgentPoolFavoriteList()

  return useMemo(
    () => agentPoolFavoriteList.includes(agentPoolId),
    [agentPoolFavoriteList, agentPoolId],
  )
}

export const useToggleAgentPoolFavorite = () => {
  const setUserProperty = useSetUserProperty()
  const agentPoolFavoriteList = useAgentPoolFavoriteList()
  const isLimitReached = useIsAgentPoolFavoriteLimitReached()

  return useCallback(
    (agentPoolId: number, value?: boolean) => {
      const nextValue = value ?? !agentPoolFavoriteList.includes(agentPoolId)

      const disabled = isLimitReached && nextValue

      if (disabled) {
        alert.error('You have reached the limit of favorite agent pools')
        return
      }

      const newAgentPoolFavoriteList = nextValue
        ? [agentPoolId, ...agentPoolFavoriteList]
        : agentPoolFavoriteList.filter(id => id !== agentPoolId)
      const joinedNewAgentPoolFavoriteList = newAgentPoolFavoriteList.join(',')

      setUserProperty(UserProperties.AGENT_POOL_FAVORITE_LIST, joinedNewAgentPoolFavoriteList)
    },
    [agentPoolFavoriteList, isLimitReached, setUserProperty],
  )
}
